import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../contexts/CityContext';
import api from '../services/api';

const CATEGORIES = [
  { id: 'mercado', label: 'Mercado', emoji: '🛒' },
  { id: 'farmacia', label: 'Farmácia', emoji: '💊' },
  { id: 'padaria', label: 'Padaria', emoji: '🥖' },
  { id: 'restaurante', label: 'Restaurante', emoji: '🍽️' },
  { id: 'servicos', label: 'Serviços', emoji: '🔧' },
  { id: 'evento', label: 'Evento', emoji: '🎉' },
  { id: 'publico', label: 'Público', emoji: '🏛️' },
  { id: 'mercado_publico', label: 'Mercado Público', emoji: '🏪' },
];

export function MerchantRequestPage() {
  const { selectedCity, cities } = useCity();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    city_id: selectedCity?.id || '',
    name: '',
    category: 'mercado',
    street: '',
    street_number: '',
    neighborhood: '',
    phone: '',
    whatsapp: '',
    description: '',
    lat: '',
    lng: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await api.post('/merchant/businesses/request', {
        ...form,
        city_id: Number(form.city_id),
        lat: form.lat ? Number(form.lat) : (selectedCity?.lat != null ? Number(selectedCity.lat) : undefined),
        lng: form.lng ? Number(form.lng) : (selectedCity?.lng != null ? Number(selectedCity.lng) : undefined),
      });
      setSuccess(`Solicitação enviada! Seu estabelecimento "${data.business.name}" está pendente de aprovação.`);
      setForm({ ...form, name: '', street: '', street_number: '', neighborhood: '', phone: '', whatsapp: '', description: '' });
    } catch (err) {
      const apiErr = err.response?.data?.error;
      setError(apiErr?.details?.[0]?.message || apiErr?.message || 'Erro ao enviar solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)',
    boxSizing: 'border-box'
  };

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-primary) 0%, var(--nc-primary-dark) 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 26px', borderRadius: '0 0 22px 22px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navigate(-1)} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Cadastrar meu comércio</h1>
        </div>
        <p style={{ fontSize: 13, opacity: 0.85, margin: 0, lineHeight: 1.5 }}>
          Envie os dados e a equipe Nossa Cidade aprovará seu estabelecimento. Enquanto isso, ele não aparece publicamente.
        </p>
      </div>

      <div style={{ padding: 16 }}>
        {success && (
          <div role="status" style={{
            padding: '12px 14px', borderRadius: 10, marginBottom: 14,
            background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)', color: 'var(--nc-success-text)', fontSize: 13, lineHeight: 1.5
          }}>
            ✅ {success}
          </div>
        )}
        {error && (
          <div role="alert" style={{
            padding: '12px 14px', borderRadius: 10, marginBottom: 14,
            background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 13
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 18 }}>
          <label style={labelStyle}>Cidade</label>
          <select value={form.city_id} onChange={set('city_id')} required style={inputStyle} aria-label="Cidade">
            <option value="" disabled>Selecione a cidade</option>
            {cities.map(c => <option key={c.id} value={c.id}>{c.name}/{c.state}</option>)}
          </select>

          <label style={labelStyle}>Nome do estabelecimento</label>
          <input type="text" value={form.name} onChange={set('name')} required minLength={2} placeholder="Ex.: Padaria Pão Nosso" style={inputStyle} aria-label="Nome do estabelecimento" />

          <label style={labelStyle}>Categoria</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, category: cat.id }))}
                aria-pressed={form.category === cat.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10,
                  border: form.category === cat.id ? '1.5px solid #0d5c3b' : '1px solid var(--nc-border)',
                  background: form.category === cat.id ? '#f0fdf4' : '#fff',
                  color: form.category === cat.id ? '#0d5c3b' : '#374151',
                  fontSize: 13, cursor: 'pointer', transition: 'all 0.15s ease'
                }}
              >
                <span style={{ fontSize: 16 }}>{cat.emoji}</span> {cat.label}
              </button>
            ))}
          </div>

          <label style={labelStyle}>Rua / Logradouro</label>
          <input type="text" value={form.street} onChange={set('street')} placeholder="Ex.: Rua Vicente Neto" style={inputStyle} aria-label="Rua / Logradouro" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Número</label>
              <input type="text" value={form.street_number} onChange={set('street_number')} placeholder="45" style={inputStyle} aria-label="Número" />
            </div>
            <div>
              <label style={labelStyle}>Bairro</label>
              <input type="text" value={form.neighborhood} onChange={set('neighborhood')} placeholder="Centro" style={inputStyle} aria-label="Bairro" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Telefone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} placeholder="(83) 99999-0000" style={inputStyle} aria-label="Telefone" />
            </div>
            <div>
              <label style={labelStyle}>WhatsApp</label>
              <input type="tel" value={form.whatsapp} onChange={set('whatsapp')} placeholder="5583999990000" style={inputStyle} aria-label="WhatsApp" />
            </div>
          </div>

          <label style={labelStyle}>Descrição</label>
          <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Conte o que seu comércio oferece..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} aria-label="Descrição" />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: loading ? 'rgba(13,92,59,0.7)' : '#0d5c3b', color: 'var(--nc-on-primary)',
              fontSize: 14, fontWeight: 600, marginTop: 4
            }}
          >
            {loading ? 'Enviando...' : 'Enviar solicitação'}
          </button>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 6
};
